import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Button, EmptyNote, ENTER, FADE, IconButton, Icons, Loading, StateNote } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useJobs } from '@/lib/api/hooks'
import { PageFrame } from '@/shell/PageFrame'
import { JobCreateDialog } from './JobCreateDialog'
import { JobGroups } from './JobGroups'
import { groupJobs, monthKey, monthName } from './jobPeriods'
import { MonthSwitcher } from './MonthSwitcher'

/**
 * The work: what is running now on top, then one month of jobs — this month
 * unless another is chosen. Other months stay out of the way until picked.
 * Archived work stays out of sight until the archive is opened. Adding a job
 * raises the invitation card — one question at a time over the list —
 * instead of growing a form in place.
 */
export function JobsPage() {
  const jobs = useJobs()
  const [adding, setAdding] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [month, setMonth] = useState(() => monthKey(new Date()))

  const { running, settled, archived } = useMemo(() => {
    const all = jobs.data ?? []
    const live = all.filter((job) => job.archivedAt === null)
    return {
      running: live.filter((job) => job.status === 'in_progress'),
      settled: live.filter((job) => job.status !== 'in_progress'),
      archived: all.filter((job) => job.archivedAt !== null),
    }
  }, [jobs.data])
  const inMonth = useMemo(() => settled.filter((job) => monthKey(new Date(job.createdAt)) === month), [settled, month])

  return (
    <PageFrame
      title={showArchived ? 'Archived work' : 'Work'}
      description={showArchived ? 'Jobs taken out of the way. Open one to restore it.' : 'Track and manage work across all projects.'}
      actions={
        <>
          <AnimatePresence initial={false}>
            {showArchived ? null : (
              <motion.div key="month" {...FADE}>
                <MonthSwitcher value={month} onChange={setMonth} />
              </motion.div>
            )}
          </AnimatePresence>
          <IconButton label={showArchived ? 'Back to the work' : 'Archived work'} size="sm" active={showArchived} onClick={() => setShowArchived((value) => !value)}>
            <Icons.archive />
          </IconButton>
          <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setAdding(true)}>
            Add job
          </Button>
        </>
      }
    >
      {jobs.isLoading ? (
        <Loading label="Loading work" />
      ) : jobs.error ? (
        <StateNote
          title="The work did not load."
          detail={describeError(jobs.error)}
          action={
            <Button size="sm" icon={<Icons.retry />} onClick={() => void jobs.refetch()}>
              Try again
            </Button>
          }
        />
      ) : (jobs.data?.length ?? 0) === 0 ? (
        <EmptyNote
          title="No work yet."
          detail="The first job of the studio is one button away."
          action={
            <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setAdding(true)}>
              Add job
            </Button>
          }
        />
      ) : (
        // Opening and leaving the archive hands the list over: one fades and settles as the other leaves.
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={showArchived ? 'archive' : 'work'} {...ENTER}>
            {showArchived ? (
              archived.length === 0
                ? <EmptyNote title="Nothing is archived." detail="Archived jobs gather here." />
                : <JobGroups groups={groupJobs(archived, 'month')} />
            ) : (
        <>
          <JobGroups
            groups={inMonth.length > 0 ? [{ key: month, label: monthName(month), detail: null, jobs: inMonth, current: true }] : []}
            lead={{ label: 'Running Now', jobs: running }}
            openAll
          />
          {inMonth.length === 0 ? (
            <p className="py-6 text-center text-caption text-muted-foreground">
              No other jobs were created in {monthName(month)}. Pick another month above.
            </p>
          ) : null}
        </>
            )}
          </motion.div>
        </AnimatePresence>
      )}

      <JobCreateDialog open={adding} onClose={() => setAdding(false)} />
    </PageFrame>
  )
}
